import { React, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { Typography, Box, ThemeProvider, createTheme } from '@common-ui/ui';
import {
  EditOutlined,
  DeleteOutlined,
  Check,
  InfoOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import PrimaryNavigation from '../../components/header/PrimaryNavigation/index';
import DeleteModal from '../../components/modals/DeleteModal';
import deleteModalEnum from '../../enums/deleteModalEnum';
import handleReportDeletion from '../../utils/handleReportDeletion';
import { useAppDispatch } from '../../store/configureStore';
import { getAllReports } from '../../store/slices/reportSlice';
import { getDisplayDateOnTextFiled } from '../../utils/getDateFormatDeprecated';
import updateDocumentTitle from '../../utils/updateDocumentTitle';
import { useSelector } from 'react-redux';
import CreateReport from './CreateReport';

const lighttheme = createTheme({
  palette: {
    mode: 'light',
  },
});

function dateComparator(v1, v2) {
  const dateA = new Date(v1);
  const dateB = new Date(v2);
  return dateA - dateB;
}

function sortReportsByDate(reports = [], field, descending = true) {
  // shallow copy using slice
  return reports.slice().sort((a, b) => dateComparator(a[field], b[field]) * (descending && -1));
}

export default function Reports({ props }) {
  updateDocumentTitle(props.location.pathname);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { allReports } = useSelector((state) => state.reportData);

  const [draftRows, setDraftRows] = useState([]);
  const [completedRows, setCompletedRows] = useState([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(false);

  useEffect(() => {
    dispatch(getAllReports());
  }, []);

  useEffect(() => {
    const { draft = [], finalized = [] } = allReports || {};
    const sortedDrafts = sortReportsByDate(draft, 'created_on');
    const sortedFinalized = sortReportsByDate(finalized, 'created_on');
    setDraftRows(sortedDrafts || []);
    setCompletedRows(sortedFinalized || []);
  }, [allReports]);

  let columns = [
    {
      field: 'options',
      headerName: 'Options',
      editable: false,
      width: 75,
      renderCell: (params) =>
        params.row.finalized === false ? (
          <>
            <EditOutlined
              sx={{ ...iconHover }}
              tooltip="edit report"
              onClick={() => {
                const data = params.row;
                history.push({
                  pathname: `/report/${params.id}`,
                  state: { data },
                });
              }}
            />
            <DeleteOutlined
              sx={{ ...iconHover }}
              tooltip="delete report"
              onClick={() => trashIconHandler(params.id)}
            />
          </>
        ) : (
          <VisibilityOutlined
            sx={{ ...iconHover }}
            tooltip="edit report"
            onClick={() => {
              const data = params.row;
              history.push({
                pathname: `/report/${params.id}`,
                state: { data },
              });
            }}
          />
        ),
    },
    {
      field: 'report_name',
      headerName: 'Report',
      editable: false,
      flex: 1,
      minWidth: 50,
    },
    {
      field: 'performed_on',
      headerName: 'Performed On',
      editable: false,
      flex: 1,
      minWidth: 50,
      valueFormatter: (params) => {
        return getDisplayDateOnTextFiled(params.value);
      },
    },
    {
      field: 'created_on',
      headerName: 'Created On',
      editable: false,
      flex: 1,
      minWidth: 50,
      valueFormatter: (params) => {
        return getDisplayDateOnTextFiled(params.value);
      },
      sortComparator: dateComparator,
    },
    {
      field: 'company',
      headerName: 'Company',
      editable: false,
      flex: 1,
      minWidth: 50,
    },
    {
      field: 'site_id',
      headerName: 'Location',
      editable: false,
      flex: 1,
      minWidth: 50,
    },
    {
      field: 'performed_by',
      headerName: 'Performed By',
      editable: false,
      flex: 1,
      minWidth: 50,
    },
  ];

  const trashIconHandler = (id) => {
    setOpenDeleteModal(true);
    setDeleteRowId(id);
  };

  const deleteButtonHandler = () => {
    setOpenDeleteModal(false);
    handleReportDeletion(deleteRowId);
  };

  return (
    <Box>
      <DeleteModal
        header={deleteModalEnum.REPORT_HEADER}
        body={deleteModalEnum.REPORT_BODY}
        notification={deleteModalEnum.REPORT_NOTIFICATION}
        apiHandler={deleteButtonHandler}
        showIcon={false}
        openModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
      />
      <PrimaryNavigation />
      <Box sx={gridContainer}>
        <Box sx={gridHeader}>
          <Typography>
            <EditOutlined /> Draft Reports
          </Typography>
          <CreateReport />
        </Box>
        <ThemeProvider theme={lighttheme}>
          <DataGrid
            getRowId={(row) => row.report_id}
            rows={draftRows}
            columns={columns}
            disableSelectionOnClick
            density="compact"
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </ThemeProvider>
      </Box>
      <Box mt={4} sx={gridContainer}>
        <Box sx={gridHeader}>
          <Typography>
            <Check /> Completed Reports
          </Typography>
          <Typography>
            <InfoOutlined /> A draft report must be finalized before creating another.
          </Typography>
        </Box>
        <ThemeProvider theme={lighttheme}>
          <DataGrid
            getRowId={(row) => row.report_id}
            rows={completedRows}
            columns={columns}
            disableSelectionOnClick
            density="compact"
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </ThemeProvider>
      </Box>
    </Box>
  );
}

const iconHover = {
  color: 'rgba(0, 0, 0, 0.87)',
  mr: 1,
  '&:hover': {
    cursor: 'pointer',
    color: 'primary.main',
    transition: '250ms',
  },
};

const gridContainer = {
  height: 400,
  width: '100%',
  p: 2,
  color: 'black',
};

const gridHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  p: 1,
};
